import {
  CatalystInsight,
  CatalystInsightFilterRequest,
} from '@api/api/catalyst-insights';
import {
  EarningsInsight,
  EarningsInsightsFilterRequest,
} from '@api/api/earnings-insights';
import {
  KgInsight,
  KgInsightFilterRequest,
  KgInsightFilterResponse,
  kgInsights,
} from '@api/api/kg-insights';
import {
  mapCatalystFilterRequestToKgFilterRequest,
  mapEarningsInsightsFilterRequestToKgFilterRequest,
  mapKgInsightToCatalystInsight,
  mapKgInsightToEarningsInsight,
} from '@terminal/services/insights/utils/catalyst-insight-to-kg-mapper';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import {
  ApiFetchResponse,
  apiFetch,
  wretchRequest,
} from '@terminal/utils/api-fetch/apiFetch';

const isKgInsightsEnabled = () =>
  useFlags.getState().getFeatureFlagState('KG-1094-kg-insights');

export const getKgInsight = (insightId: string) => {
  const { path, schema } = isKgInsightsEnabled()
    ? kgInsights.getKgInsightV2
    : kgInsights.getKgInsight;
  return wretchRequest<KgInsight>(path(insightId), {
    schema,
  });
};

export const getKgInsightFilterForChart = async (
  payload: KgInsightFilterRequest
) => {
  const { path, schema } = kgInsights.postKgInsightFilter;

  return await apiFetch<KgInsightFilterResponse>(path, {
    method: 'post',
    body: payload,
    schema,
  });
};

export const getKgInsightFilter = (
  body: KgInsightFilterRequest,
  signal?: AbortSignal
) => {
  const { path, schema } = kgInsights.postKgInsightFilter;

  return wretchRequest<KgInsightFilterResponse>(path, {
    method: 'post',
    body,
    schema,
    signal,
  });
};

export const getCatalystInsightListFromKg = async (
  payload: CatalystInsightFilterRequest,
  signal?: AbortSignal
): Promise<CatalystInsight[]> => {
  const response = await getKgInsightFilter(
    mapCatalystFilterRequestToKgFilterRequest(payload),
    signal
  );

  return response.map(mapKgInsightToCatalystInsight);
};

export const getKgCompanyCatalystInsightListForChart = async (
  payload: CatalystInsightFilterRequest
): Promise<ApiFetchResponse<CatalystInsight[]>> => {
  const response = await getKgInsightFilterForChart(
    mapCatalystFilterRequestToKgFilterRequest(payload)
  );

  if (response.error) {
    return {
      error: response.error,
      validationError: false,
    };
  }

  return {
    data: response.data.map(mapKgInsightToCatalystInsight),
    validationError: response.validationError,
  };
};

export const getKgEarningsInsightsForChart = async (
  payload: EarningsInsightsFilterRequest
): Promise<ApiFetchResponse<EarningsInsight[]>> => {
  const response = await getKgInsightFilterForChart(
    mapEarningsInsightsFilterRequestToKgFilterRequest(payload)
  );

  if (response.error) {
    return {
      error: response.error,
      validationError: false,
    };
  }

  return {
    data: response.data
      .filter(insight => !!insight.subject.ref?.id)
      .map(mapKgInsightToEarningsInsight),
    validationError: response.validationError,
  };
};
